import { portalText, usePortalTranslation } from "../i18n/portal.js";
/**
 * The workspace switcher in the top bar.
 *
 * A workspace is the container inside an account (C5), and switching one is the single
 * most consequential control in the shell: every figure below it is scoped to the
 * workspace, so the current one is named on the trigger and ticked in the menu rather
 * than merely highlighted.
 *
 * AMENDED 2026-09-25 (customer-portal review #148, #153, #159): picking another workspace switches
 * to it (`useSwitchWorkspace`, owned by the shell); the current one is not a button that does
 * nothing. A long name is cut with an ellipsis, and the count reads "1 person" / "4 people".
 *
 * AMENDED 2026-10-05 (plan-014 A4, FR12-AUTH-10): renamed the workspace PICKER, and it lists only the
 * workspaces the person can ENTER — their memberships in this organization that are not archived,
 * from the auth_session's fresh access listing — never every workspace the organization has (an Owner
 * or Admin sees the others on the Organization page, as metadata). The figures (people, this month's
 * spend) come from the workspace listing where it carries the workspace. "New workspace" is offered
 * only to an organization role that may create one (`account.workspace.create`).
 *
 * AMENDED 2026-10-07 (customer-portal review #150, merged from main into plan-014): the current
 * workspace comes from the workspace listing, which may still be on its way or may have failed. The
 * trigger then says so, and the menu offers Try again with the reason (and the request id when
 * WeTalk itself failed); the current row is ticked by the sign-in's own workspace id.
 *
 * AMENDED 2026-10-09 (design sync): the design dropped its Workspaces screen; "Manage workspaces" opens
 * Organization → Workspaces, and "New workspace" opens the same tab with its New workspace dialog open.
 *
 * AMENDED 2026-11-03 (owner, "on invite on organization it says no workspace open and i cannot select from
 * dropdown"): with the organization alone open (`currentId` null) the picker is still drawn — the trigger says
 * "No workspace open", no row is ticked, and picking a workspace switches into it. When there is none the person
 * may enter, the menu says so in words instead of showing an empty list.
 */
import { Link } from "@tanstack/react-router";
import { Icon } from "@wetalk/ui";
import type { ReactNode } from "react";

import { GLYPH } from "./glyph.js";
import { usePopover } from "./popover.js";
import { formatMoney } from "../money.js";
import type { WorkspaceChoiceView, WorkspaceView } from "../api/gateway.js";

export interface WorkspacePickerProps {
  /** The signed-in workspace from the listing, or `null` while it is read or when it failed. */
  readonly current: WorkspaceView | null;
  /** The signed-in workspace's id, from the sign-in itself; `null` with the organization alone open. */
  readonly currentId: string | null;
  /** Why the listing could not be read, or `null`. */
  readonly problem: string | null;
  /** Reads the listing again. */
  readonly onRetry: () => void;
  /** The workspaces the person can enter in this organization (archived ones already left out). */
  readonly enterable: readonly WorkspaceChoiceView[];
  /** The organization's workspace listing, for each row's figures; rows it lacks show none. */
  readonly figure: readonly WorkspaceView[];
  /** Switches to another workspace. Never called for the current one. */
  readonly onPick: (workspaceId: string) => void;
  /** True while a switch is on its way: the items are disabled so it is not asked for twice. */
  readonly pending: boolean;
  /** Whether the person's organization role may create a workspace (`account.workspace.create`). */
  readonly mayCreate: boolean;
}

/** "3 people · €1,559.00 this month", or `null` when the listing does not carry the workspace. */
function figureLineOf(workspaceId: string, figure: readonly WorkspaceView[]): string | null {
  const found = figure.find((one) => one.workspaceId === workspaceId);
  if (found === undefined) return null;
  // The prototype's `{people} · {agents} · {spend}`; the listing carries no agent count, so that part is not drawn.
  return portalText("shell", "workspace.figure", { count: found.memberCount, spend: formatMoney(found.spendThisMonth) });
}

export function WorkspacePicker({
  current,
  currentId,
  problem,
  onRetry,
  enterable,
  figure,
  onPick,
  pending,
  mayCreate,
}: WorkspacePickerProps): ReactNode {
  usePortalTranslation("shell");
  const { open, anchorRef, triggerRef, toggle, close } = usePopover();
  const noneOpen = currentId === null;
  const name = noneOpen
    ? portalText("shell", "scope.noWorkspace")
    : current?.name ?? (problem === null ? portalText("shell", "reading_your_workspace_28eb5762") : portalText("shell", "workspace_could_not_be_read_7e550753"));

  return (
    <div className="c-pop-anchor" ref={anchorRef}>
      <button
        type="button"
        ref={triggerRef}
        className="c-ws-trigger"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={toggle}
      >
        {noneOpen ? null : <span className="c-ws-mark">{current?.initials ?? (problem === null ? "…" : "?")}</span>}
        <span className="c-ws-name" title={name}>
          {name}
        </span>
        <span className="c-ws-caret">
          <Icon glyph={GLYPH.caretDown} size="xs" tone="muted" />
        </span>
      </button>

      {open ? (
        <div className="c-pop c-pop-left" role="menu" aria-label={portalText("shell", "switch_workspace_6d00569e")}>
          <p className="c-pop-head">{portalText("shell", "switch_workspace_6d00569e")}</p>
          {problem === null ? null : (
            <button
              type="button"
              role="menuitem"
              className="c-pop-item"
              onClick={() => {
                onRetry();
                close();
              }}
            >
              <Icon glyph={GLYPH.arrowsClockwise} size="md" tone="accent" />
              <span className="c-pop-item-body">
                <span className="c-pop-item-title">{portalText("shell", "try_again_042c862e")}</span>
                <span className="c-pop-item-dek">{problem}</span>
              </span>
            </button>
          )}
          {enterable.length === 0 ? <p className="c-ws-none">{portalText("shell", "workspace.noneToEnter")}</p> : null}
          {enterable.map((one) => {
            const line = figureLineOf(one.workspaceId, figure);
            return (
              <button
                type="button"
                role="menuitemradio"
                aria-checked={one.workspaceId === currentId}
                key={one.workspaceId}
                className="c-pop-item"
                disabled={pending}
                title={one.name}
                onClick={() => {
                  if (one.workspaceId !== currentId) onPick(one.workspaceId);
                  close();
                }}
              >
                <span className="c-mark">{one.initials}</span>
                <span className="c-pop-item-body">
                  <span className="c-pop-item-title c-ws-pop-name">{one.name}</span>
                  {line === null ? null : <span className="c-pop-item-dek">{line}</span>}
                </span>
                {one.workspaceId === currentId ? (
                  <Icon glyph={GLYPH.check} size="sm" tone="accent" label={portalText("shell", "current_workspace_d60e0b97")} />
                ) : null}
              </button>
            );
          })}
          <div className="c-pop-rule" />
          {mayCreate ? (
            <Link to="/organization/workspaces" search={{ new: true }} role="menuitem" className="c-pop-item" onClick={close}>
              <Icon glyph={GLYPH.plus} size="md" tone="accent" />
              <span className="c-pop-item-body">
                <span className="c-pop-item-title">{portalText("shell", "new_workspace_b21b860f")}</span>
              </span>
            </Link>
          ) : null}
          <Link to="/organization/workspaces" role="menuitem" className="c-pop-item" onClick={close}>
            <Icon glyph={GLYPH.gearSix} size="md" tone="muted" />
            <span className="c-pop-item-body">
              <span className="c-pop-item-title">{portalText("shell", "manage_workspaces_d075f7bf")}</span>
            </span>
          </Link>
        </div>
      ) : null}
    </div>
  );
}
